import type { EvidencePolicyDto } from '@enshield/domain';

export type EvidenceQueueStatus = 'queued' | 'uploading' | 'succeeded' | 'failed';

export interface EvidenceQueueItem {
  id: string;
  file: File;
  status: EvidenceQueueStatus;
  progress: number;
  error?: string;
}

export type EvidenceQueueAction =
  | { type: 'add'; items: EvidenceQueueItem[] }
  | { type: 'remove'; id: string }
  | { type: 'uploading'; id: string }
  | { type: 'progress'; id: string; progress: number }
  | { type: 'succeeded'; id: string }
  | { type: 'failed'; id: string; error: string }
  | { type: 'retry'; id: string };

export function evidenceFileIdentity(file: File): string {
  return `${file.name}:${file.size}:${file.type}:${file.lastModified}`;
}

export function createEvidenceQueueItems(files: Iterable<File>): EvidenceQueueItem[] {
  return Array.from(files, (file) => ({
    id: evidenceFileIdentity(file),
    file,
    status: 'queued' as const,
    progress: 0,
  }));
}

export function validateEvidenceSelection(
  files: Iterable<File>,
  queue: EvidenceQueueItem[],
  policy: EvidencePolicyDto,
  persisted: { count: number; totalBytes: number },
): { accepted: File[]; errors: string[] } {
  const accepted: File[] = [];
  const errors: string[] = [];
  const identities = new Set(queue.map((entry) => entry.id));
  let count = persisted.count + queue.length;
  let totalBytes = persisted.totalBytes + queue.reduce((sum, entry) => sum + entry.file.size, 0);

  for (const file of files) {
    const identity = evidenceFileIdentity(file);
    if (identities.has(identity)) {
      errors.push(`${file.name} is already selected.`);
      continue;
    }
    /*
     * An empty `file.type` is the browser not knowing, not a bad file.
     *
     * A camera photo picked on Android Chrome reports `''`, and this rejected
     * it outright — so the shopper on the claim wizard was blocked before the
     * upload even started, told their photograph was "not a supported file
     * type". The two sibling validators already skip the check in that case;
     * this one, which the wizard actually uses, did not. The server sniffs the
     * bytes and is the authority either way.
     */
    if (file.type && !policy.acceptedContentTypes.includes(file.type)) {
      errors.push(`${file.name} is not a supported file type.`);
      continue;
    }
    if (file.size <= 0) {
      errors.push(`${file.name} is empty.`);
      continue;
    }
    if (file.size > policy.maxFileBytes) {
      errors.push(`${file.name} exceeds the ${policy.maxFileBytes} bytes per-file limit.`);
      continue;
    }
    if (count + 1 > policy.maxFilesPerClaim) {
      errors.push(`A claim can carry at most ${policy.maxFilesPerClaim} files.`);
      continue;
    }
    if (totalBytes + file.size > policy.maxTotalBytesPerClaim) {
      errors.push(`The total evidence size cannot exceed ${policy.maxTotalBytesPerClaim} bytes.`);
      continue;
    }

    accepted.push(file);
    identities.add(identity);
    count += 1;
    totalBytes += file.size;
  }

  return { accepted, errors };
}

export function evidenceQueueReducer(
  state: EvidenceQueueItem[],
  action: EvidenceQueueAction,
): EvidenceQueueItem[] {
  if (action.type === 'add') {
    const existing = new Set(state.map((entry) => entry.id));
    return [...state, ...action.items.filter((entry) => !existing.has(entry.id))];
  }
  if (action.type === 'remove') {
    return state.some((entry) => entry.id === action.id)
      ? state.filter((entry) => entry.id !== action.id)
      : state;
  }
  return state.map((entry) => {
    if (entry.id !== action.id) return entry;
    switch (action.type) {
      case 'uploading':
        return { ...entry, status: 'uploading', progress: 0, error: undefined };
      case 'progress':
        return { ...entry, progress: Math.max(0, Math.min(100, action.progress)) };
      case 'succeeded':
        return { ...entry, status: 'succeeded', progress: 100, error: undefined };
      case 'failed':
        return { ...entry, status: 'failed', error: action.error };
      case 'retry':
        return { ...entry, status: 'queued', progress: 0, error: undefined };
    }
  });
}
